import { useState } from 'react';
import {
  ActionIcon,
  Button,
  Card,
  Divider,
  Group,
  Paper,
  Popover,
  Stack,
  Text,
  createStyles,
} from '@mantine/core';
import {
  IconDotsVertical,
  IconHeart,
  IconHeartFilled,
  IconMessageCircle2,
  IconPin,
} from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { useTheme } from '@emotion/react';

import { FEATURE } from '~/common/enums/feature.enum';
import { getTimeAgoTranslation } from '~/components/utils/formatters';
import { getPrimaryThemeColor } from '~/config/themeUtils';
import { getCdnUrl } from '~/domains/assets/utils/getCdnUrl';
import { Avatar } from '~/domains/common/components/Avatar/Avatar';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';

import type { Channel } from '../../interfaces/channel.interface';
import { SelfFetchingContestRow } from '../Contests/ContestRow';
import type { Post as PostType } from '../../interfaces/post.interface';

import CommentSection from './CommentSection';
import { usePostActions } from './hooks/usePostActions';

type PostProps = {
  post: PostType;
  channel: Channel;
};

const useStyles = createStyles({
  postBody: { wordBreak: 'break-word' },
});

/** @deprecated use PostsNew instead */
function Post({ post, channel }: PostProps) {
  const theme = useTheme();
  const [commentsOpened, setIsCommentsOpened] = useState(false);

  const { key, interval } = getTimeAgoTranslation(post.createdAt);
  const { t } = useTranslation('commissioner');
  const { classes } = useStyles();
  const reportPostEnabled = useIsFeatureEnabled(FEATURE.ENABLE_REPORT_POST);

  const { handleLikeClick, isLoadingLike, handleUnlikeClick, isLoadingUnlike, handleReportPost } =
    usePostActions({ id: post.id });

  const avatar =
    channel?.avatarUrl || getCdnUrl('/images/commissioner/DefaultCommissionerAvatar.png');

  return (
    <Paper p="xl">
      {post.isPinned && (
        // 8px to align with channel.name
        <Group spacing={8} ml="xl">
          <IconPin size={16} color={getPrimaryThemeColor(theme)} />
          <Text size={12} color="dimmed">
            {t('hub.posts.pinned')}
          </Text>
        </Group>
      )}
      <Group noWrap position="apart">
        <Group align="start" spacing="xs" mb="md">
          <Avatar src={avatar} radius={50} />
          <Stack spacing={0}>
            <Text maw={300} truncate="end" fw={700} size={12}>
              {channel?.name}
            </Text>
            <Text color="dimmed" size={12}>
              {post.createdAt && t(key, { count: interval })}
            </Text>
          </Stack>
        </Group>
        {reportPostEnabled && (
          <Popover position="bottom-end" trapFocus>
            <Popover.Target>
              <ActionIcon>
                <IconDotsVertical />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack>
                <Button
                  variant="subtle"
                  onClick={() => handleReportPost()}
                  styles={{ root: { ':hover': { background: 0 } } }}
                >
                  <Text color={getPrimaryThemeColor(theme)}>{t('hub.posts.report')}</Text>
                </Button>
              </Stack>
            </Popover.Dropdown>
          </Popover>
        )}
      </Group>

      <Stack spacing="xs" w="100%">
        <Text size={26} fw={700} truncate="end">
          {post.title}
        </Text>
        <Text size={16} className={classes.postBody}>
          {post.body}
        </Text>
        {post.contestSlug && (
          <Card withBorder shadow="xl" mt="md">
            <SelfFetchingContestRow contestId={post.contestSlug} showCta isStacked />
          </Card>
        )}
        {(post.allowLikes || post.allowComments) && <Divider mt="sm" mb="lg" />}
      </Stack>
      <Group position="apart">
        <Group spacing={40}>
          {post.allowLikes && (
            <Group spacing="sm">
              <ActionIcon
                disabled={isLoadingLike || isLoadingUnlike || !post.id}
                onClick={() =>
                  post.isLiked ? handleUnlikeClick(post.id) : handleLikeClick(post.id)
                }
              >
                {post.isLiked ? <IconHeartFilled /> : <IconHeart />}
              </ActionIcon>
              <Text>{post.likesCount}</Text>
            </Group>
          )}
          {post.allowComments && (
            <Group spacing="sm">
              <ActionIcon onClick={() => setIsCommentsOpened(!commentsOpened)}>
                <IconMessageCircle2 />
              </ActionIcon>
              <Text>{post.commentsCount}</Text>
            </Group>
          )}
        </Group>
      </Group>
      {commentsOpened && post.id && <CommentSection postId={post.id} />}
    </Paper>
  );
}

export default Post;
